import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Link,
  Paper,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { useEffect, useState } from "react";
import { useAdminStore } from "../../../../context/admin-store";
import { useCategory } from "../../../../context/category-store";
import {
  getAllSubscriptions,
  getSubscriptionStatus,
} from "../../../../services/subscriptions.service";
import SectionTitle from "../../../../ui-components/SectionTitle";
import { parseDate } from "../../../../utils/parseDate";
import UpdateButton from "./OperationButtons/UpdateButton";
import ViewButton from "./OperationButtons/ViewButton";

import SearchIcon from "@mui/icons-material/Search";
import { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "../../../ui/DataTable";
import {
  ExportColumnConfig,
  ExportDialog,
} from "../../../ui/ExportDialog";
import { adminButtonPrimarySx } from "../../modalStyle";

const columns: ColumnDef<any>[] = [
  {
    accessorKey: "id",
    header: "ID",
  },
  {
    accessorKey: "name",
    header: "Nome",
  },
  {
    accessorKey: "brand_name",
    header: "Marca",
  },
  {
    accessorKey: "instagram_url",
    header: "Instagram",
    cell: (info) => info.getValue(),
  },
  {
    accessorKey: "category",
    header: "Categoria",
  },
  {
    accessorKey: "start_date",
    header: "Data Entrada",
  },
  {
    accessorKey: "end_date",
    header: "Data Saida",
  },
  {
    accessorKey: "space_type",
    header: "Tipo Espaco",
  },
  {
    accessorKey: "created_at",
    header: "Data da Pré-Inscrição",
  },
  {
    accessorKey: "status_id",
    header: "Status",
  },
  {
    accessorKey: "actions",
    header: "Ações",
    cell: (info) => info.getValue(),
  },
];

interface PreSubscription {
  id: number;
  user_id: number;
  event_id: number;
  first_time: boolean;
  start_date: Date;
  end_date: Date;
  status_id: number;
  created_at: Date;
  space_type: string;
}

//nome marca instagram categoria

const createRows = (
  events: PreSubscription[],
  updateSubscriptions: Function,
  users: any,
  categories: any,
) => {
  return events.map(
    (
      {
        event_id,
        created_at,
        user_id,
        first_time,
        start_date,
        id,
        end_date,
        status_id,
        space_type,
      },
      i,
      arr,
    ) => {
      const user = users.find((user) => user.id === user_id) || {};
      const category =
        categories.find((cat) => cat.id === (user as any).category_id) || null;
      const rawInstagram = ((user as any)?.instagram_url || "")
        .toString()
        .trim();
      const username = rawInstagram
        ? rawInstagram.startsWith("http")
          ? rawInstagram
              .replace(/^https?:\/\/www\.instagram\.com\//, "")
              .replace(/\/$/, "")
          : rawInstagram.replace(/^@/, "")
        : "";
      const igHref = username
        ? `https://www.instagram.com/${username}/`
        : undefined;
      return {
        id,
        event_id,
        name: (user as any)?.name || "",
        brand_name: (user as any)?.brand_name || "",
        created_at: parseDate(created_at),
        instagram_url: (
          <Link
            target={"_blank"}
            rel={"noopener noreferrer"}
            href={igHref || "#"}
          >
            {rawInstagram || "-"}
          </Link>
        ),
        category: (category && (category as any).name) || "",
        user_id,
        first_time: first_time ? "Sim" : "Não",
        start_date: parseDate(start_date),
        end_date: parseDate(end_date),
        space_type:
          space_type === "autoral"
            ? "Marcas Autorais"
            : space_type === "alimentacao"
              ? "Praça de Alimentação"
              : space_type === "1_tendas"
                ? "1 Tenda"
                : space_type === "2_tendas"
                  ? "2 Tendas"
                  : space_type === "exclusive"
                    ? "Exclusivo"
                    : space_type === "shared"
                      ? "Compartilhado"
                      : space_type,
        status_id: getSubscriptionStatus(status_id),
        actions: (
          <div className="flex flex-row gap-1">
            <UpdateButton
              id={id}
              updateSubscriptions={updateSubscriptions}
              sub={arr[i]}
            />
            <ViewButton
              id={id}
              updateEvents={() => {}}
              subscription={arr[i]}
            ></ViewButton>
          </div>
        ),
      };
    },
  );
};

export default function PreSubscriptionTable() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [deniedSubscriptions, setDeniedSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventView, setEventView] = useState<
    Record<number, "avaliacao" | "negadas">
  >({});
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [eventSearch, setEventSearch] = useState<string>("");
  const [eventStatusTab, setEventStatusTab] = useState<"active" | "finished">(
    "active",
  );

  const users = useAdminStore((state) => state.users);
  const categories = useCategory((state) => state.categories);
  const events = useAdminStore((state) => state.events);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [exportOpen, setExportOpen] = useState(false);

  const exportColumns: ExportColumnConfig[] = [
    { key: "id", label: "ID" },
    { key: "user_id", label: "Usuario ID" },
    { key: "event_id", label: "Evento ID" },
    { key: "notes", label: "Notas" },
    {
      key: "space_type",
      label: "Tipo Espaco",
      formatter: (v) =>
        v === "1_tendas"
          ? "1 Tenda"
          : v === "2_tendas"
            ? "2 Tendas"
            : v === "exclusive"
              ? "Exclusivo"
              : v === "shared"
                ? "Compartilhado"
                : v === "autoral"
                  ? "Marcas Autorais"
                  : v === "alimentacao"
                    ? "Praça de Alimentação"
                    : v,
    },
    { key: "items", label: "Items" },
    { key: "category_name", label: "Categoria" },
    { key: "event_space", label: "Espaco Evento" },
    { key: "start_date", label: "Data Inicio" },
    { key: "end_date", label: "Data Fim" },
    {
      key: "extra_table",
      label: "Mesa Extra",
      formatter: (v) => (+v ? "Sim" : "Nao"),
    },
    { key: "status_id", label: "Status ID" },
    {
      key: "is_paid",
      label: "Pagamento Efetuado",
      formatter: (v) => (v ? "Sim" : "Nao"),
    },
    { key: "pix_code", label: "Codigo PIX" },
    {
      key: "extra_chair",
      label: "Cadeira Extra",
      formatter: (v) => (+v ? "Sim" : "Nao"),
    },
    { key: "amount", label: "Valor" },
    { key: "table_quantity", label: "Quantidade Mesas" },
    { key: "chair_quantity", label: "Quantidade Cadeiras" },
    { key: "created_at", label: "Criado Em" },
    { key: "updated_at", label: "Atualizado Em" },
    { key: "event_name", label: "Nome Evento" },
    { key: "user_name", label: "Nome Usuario" },
  ];

  function getSubscriptions() {
    setLoading(true);
    getAllSubscriptions()
      .then((x) => {
        setSubscriptions(x.filter((x: any) => x.status_id === 0));
        setDeniedSubscriptions(x.filter((x: any) => x.status_id === 2));
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    getSubscriptions();
  }, []);

  // Select first event by default when events are available
  useEffect(() => {
    const baseEvents = (events || []).filter((ev) =>
      eventStatusTab === "active"
        ? Boolean((ev as any)?.active)
        : !(ev as any)?.active,
    );
    if (baseEvents.length > 0) {
      if (
        selectedEventId === null ||
        !baseEvents.some((e) => e.id === selectedEventId)
      ) {
        setSelectedEventId(baseEvents[0].id);
      }
    } else {
      setSelectedEventId(null);
    }
  }, [events, eventStatusTab, selectedEventId]);

  // Adjust selected event when filtering via search
  useEffect(() => {
    const baseEvents = (events || []).filter((ev) =>
      eventStatusTab === "active"
        ? Boolean((ev as any)?.active)
        : !(ev as any)?.active,
    );
    const normalized = eventSearch.trim().toLowerCase();
    const visible = baseEvents.filter((ev) => {
      const nameMatch = (ev.name || "").toLowerCase().includes(normalized);
      const dateStr = parseDate(ev.starts_at || "");
      const dateMatch = (dateStr || "").toLowerCase().includes(normalized);
      return !normalized || nameMatch || dateMatch;
    });
    if (visible.length === 0) {
      setSelectedEventId(null);
    } else if (
      selectedEventId === null ||
      !visible.some((e) => e.id === selectedEventId)
    ) {
      setSelectedEventId(visible[0].id);
    }
  }, [eventSearch, events, eventStatusTab, selectedEventId]);

  const normalized = eventSearch.trim().toLowerCase();
  const statusFilteredEvents = (events || []).filter((ev) =>
    eventStatusTab === "active"
      ? Boolean((ev as any)?.active)
      : !(ev as any)?.active,
  );
  const visibleEvents = statusFilteredEvents.filter((ev) => {
    const nameMatch = (ev.name || "").toLowerCase().includes(normalized);
    const dateStr = parseDate(ev.starts_at || "");
    const dateMatch = (dateStr || "").toLowerCase().includes(normalized);
    return !normalized || nameMatch || dateMatch;
  });

  // Exporta apenas o evento e a visão (avaliação/negadas) exibidos na tabela
  function getExportData() {
    const source =
      (eventView[selectedEventId] || "avaliacao") === "avaliacao"
        ? subscriptions
        : deniedSubscriptions;
    return source
      .filter((sub: any) => Number(sub.event_id) === Number(selectedEventId))
      .map((obj) => ({
        ...obj,
        space_type: obj.space_type,
        category_name:
          categories.find((cat: any) => cat.id === obj.category_id)?.name || "",
        event_name:
          events.find((ev) => ev.id === obj.event_id)?.name || "EVENTO APAGADO",
        user_name: users.find((ev) => ev.id === obj.user_id)?.name,
        extra_table: obj.extra_table,
        extra_chair: obj.extra_chair,
      }));
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          gap: isMobile ? 0 : 2,
          width: "100%",
          mb: 2,
        }}
      >
        <SectionTitle title="Pré-aprovações" />
        <Button
          variant="contained"
          onClick={() => setExportOpen(true)}
          sx={adminButtonPrimarySx}
          fullWidth={isMobile}
        >
          EXPORTAR DADOS COMO EXCEL
        </Button>
      </Box>
      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          border: "1px solid #e2e8f0",
          borderRadius: 2,
          boxShadow: "0 1px 2px rgba(16,24,40,.06)",
          mb: 3,
        }}
      >
        {events && events.length > 0 ? (
          <>
            <Box sx={{ mb: 2 }}>
              <Tabs
                value={eventStatusTab}
                onChange={(_, val) => setEventStatusTab(val)}
                sx={{
                  minHeight: 36,
                  "& .MuiTab-root": {
                    minHeight: 36,
                    textTransform: "none",
                    fontWeight: 600,
                  },
                }}
              >
                <Tab
                  value="active"
                  label={`Eventos ativos (${
                    (events || []).filter((e: any) => Boolean(e?.active)).length
                  })`}
                />
                <Tab
                  value="finished"
                  label={`Eventos finalizados (${
                    (events || []).filter((e: any) => !e?.active).length
                  })`}
                />
              </Tabs>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column-reverse", md: "row" },
                alignItems: { xs: "stretch", md: "center" },
                justifyContent: "space-between",
                gap: 2,
                mb: 2,
              }}
            >
              <Tabs
                value={selectedEventId ?? false}
                onChange={(_, val) => setSelectedEventId(val)}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Eventos"
                sx={{ borderBottom: "1px solid #e2e8f0", flex: 1, minWidth: 0 }}
              >
                {visibleEvents.map((event) => (
                  <Tab
                    key={event.id}
                    label={`${event.name} - ${parseDate(event.starts_at)}`}
                    value={event.id}
                  />
                ))}
              </Tabs>
              <TextField
                value={eventSearch}
                onChange={(e) => setEventSearch(e.target.value)}
                placeholder={"Pesquisar eventos"}
                size="small"
                sx={{ width: { xs: "100%", md: 360 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {visibleEvents.length === 0 ? (
              <Box py={4} textAlign="center">
                Nenhum evento encontrado.
              </Box>
            ) : (
              selectedEventId !== null && (
                <Grid container my={3}>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <ToggleButtonGroup
                        exclusive
                        value={eventView[selectedEventId] || "avaliacao"}
                        onChange={(_, val) => {
                          if (val)
                            setEventView((prev) => ({
                              ...prev,
                              [selectedEventId!]: val,
                            }));
                        }}
                        sx={{
                          "& .MuiToggleButton-root": {
                            textTransform: "none",
                            px: 2,
                          },
                        }}
                      >
                        <ToggleButton value="avaliacao">
                          Em Avaliação
                        </ToggleButton>
                        <ToggleButton value="negadas">Negadas</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    <Box sx={{ width: "100%" }}>
                      <DataTable
                        loading={loading}
                        data={createRows(
                          (eventView[selectedEventId] || "avaliacao") ===
                            "avaliacao"
                            ? subscriptions.filter(
                                (sub: any) =>
                                  Number(sub.event_id) ===
                                  Number(selectedEventId),
                              )
                            : deniedSubscriptions.filter(
                                (sub: any) =>
                                  Number(sub.event_id) ===
                                  Number(selectedEventId),
                              ),
                          getSubscriptions,
                          users,
                          categories,
                        )}
                        columns={columns}
                        dense={isMobile}
                      />
                    </Box>
                  </Grid>
                </Grid>
              )
            )}
          </>
        ) : (
          <Box py={4} textAlign="center">
            Nenhum evento disponível.
          </Box>
        )}
      </Paper>
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Exportar Pré-Inscrições"
        data={getExportData()}
        columns={exportColumns}
        defaultFileName="PreInscricoes.xlsx"
        sheetName="PreSubscriptions"
      />
    </>
  );
}
